/*
| Wire values, normalised.
|
| The public API spells time three ways (epoch-ms numbers, epoch-ms strings and
| ISO 8601) and money as decimal strings or raw integer strings in the asset's
| smallest unit. Screens read through these so null stays null — an unmeasured
| figure must never become a zero on its way to the page.
*/

/** Epoch milliseconds from a number, a numeric string or an ISO string; null otherwise. */
export function toMs(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const text = String(value).trim();
  if (/^-?\d+$/.test(text)) return Number(text);
  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? null : parsed;
}

/** A finite number, or null for anything absent or unparsable. */
export function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * A raw integer amount (smallest unit) as a decimal string, shifted exactly.
 * '7721962' at 6 decimals is '7.721962'. Null when either side is unknown.
 */
export function scaleRaw(raw, decimals) {
  if (raw === null || raw === undefined || decimals === null || decimals === undefined) return null;
  const text = String(raw).trim();
  if (!/^-?\d+$/.test(text)) return null;

  const negative = text.startsWith('-');
  const digits = (negative ? text.slice(1) : text).replace(/^0+(?=\d)/, '');
  const places = Number(decimals);
  if (!Number.isInteger(places) || places < 0) return null;

  const padded = digits.padStart(places + 1, '0');
  const whole = padded.slice(0, padded.length - places);
  const fraction = places ? padded.slice(-places).replace(/0+$/, '') : '';

  return `${negative ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''}`;
}

/** Display-only sum of decimal strings; null when nothing was measured. */
export function sumDecimals(values) {
  let total = null;
  for (const value of values ?? []) {
    const n = toNumber(value);
    if (n === null) continue;
    total = (total ?? 0) + n;
  }
  return total;
}
